/**
 * RootErrorBoundary — o limite que cobre o que `ModuleErrorBoundary` não cobre.
 *
 * ---------------------------------------------------------------------------
 * O ESCOPO QUE FALTAVA, medido.
 *
 * `AppShell` põe o `ModuleErrorBoundary` DENTRO do `<main>`, e a posição está
 * certa pela razão escrita lá: subir o boundary levaria o menu junto, e o menu é
 * justamente o que precisa continuar de pé para o operador ir a outra tela.
 *
 * A consequência é que metade da moldura ficava fora de qualquer boundary:
 * `Topbar`, `Sidebar` e o cálculo de `badges` — que consome `fetch` e tem, ele
 * também, uma linha que pode vir com um campo a menos. E nem `App` nem `main`
 * tinham um.
 *
 * Reproduzido na revisão, com um `throw` injetado em `Sidebar`:
 *
 *     document.body.innerText.length === 0
 *     zero links de menu
 *
 * O mesmo portal em branco que o boundary de módulo existe para impedir, por
 * outra porta. A saída não é mover aquele boundary — é ESTE, na raiz.
 *
 * ---------------------------------------------------------------------------
 * POR QUE O FALLBACK É MÍNIMO, e mínimo é requisito e não economia.
 *
 * Quando este boundary dispara, o que quebrou pode ser `ThemeProvider`,
 * `AuthProvider`, o roteador ou o próprio `global.css` não ter carregado. Então
 * o fallback:
 *
 *  - não usa `Link` nem `useNavigate` (o roteador pode ser o que quebrou);
 *  - não usa classe do `global.css` (a folha pode não estar lá) — CSS inline com
 *    `var(--nx-*)` E fallback literal, que degrada sozinho;
 *  - não lê contexto, rota nem dado;
 *  - não chama `String(erro)` sem rede de proteção — a normalização mora em
 *    `descricaoDoLancado`, e é ela que sobrevive a `throw undefined`.
 *
 * O TEXTO segue a mesma disciplina do boundary de módulo: num portal de
 * operação, "algo deu errado" é lido como soluço. A frase diz a coisa
 * verdadeira — **o portal não está exibindo nada, nenhuma tela está no ar, e
 * nada do que você viu antes desta mensagem pode ser considerado atual.**
 *
 * `role="alert"` e não `status`: é falha.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { descricaoDoLancado } from '../lib/erro-lancado.js';

interface Props {
  children: ReactNode;
}

interface State {
  /**
   * A SENTINELA É UM BOOLEANO, e não o valor lançado.
   *
   * `null` e `undefined` são valores LANÇÁVEIS. Usar `erro === null` como "não
   * houve erro" é a mesma confusão entre AUSÊNCIA e OCORRÊNCIA que o módulo de
   * frota persegue nos dados — e foi exatamente ela que fez o boundary de módulo
   * renderizar a tela de falha e estourar dentro dela.
   */
  capturou: boolean;
  erro: unknown;
}

export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { capturou: false, erro: undefined };

  /** React entrega `unknown`. A assinatura diz `unknown`. */
  static getDerivedStateFromError(erro: unknown): State {
    return { capturou: true, erro };
  }

  override componentDidCatch(erro: unknown, info: ErrorInfo): void {
    const { texto, foiError } = descricaoDoLancado(erro);
    console.error(
      `[nxvolt] Falha na RAIZ do portal: ${texto}${foiError ? '' : ' (o valor lançado não era um Error)'}`,
      erro,
      info.componentStack,
    );
  }

  override render(): ReactNode {
    if (!this.state.capturou) return this.props.children;
    const { texto, foiError } = descricaoDoLancado(this.state.erro);
    return (
      <div
        role="alert"
        style={{
          margin: '0 auto',
          maxWidth: '62ch',
          padding: '2rem 1.25rem',
          fontFamily: 'var(--nx-font-sans, system-ui, sans-serif)',
          color: 'var(--nx-ink, #1e2130)',
          background: 'var(--nx-bg, #ffffff)',
          lineHeight: 1.55,
        }}
      >
        <h1 style={{ margin: '0 0 1rem', fontSize: '1.25rem', color: 'var(--nx-alert-ink, #b91c1c)' }}>
          O portal não está exibindo nenhuma tela
        </h1>
        <p style={{ margin: '0 0 1rem', fontSize: '.9rem' }}>
          A falha aconteceu fora das telas — na moldura do portal —, então{' '}
          <strong>nenhum módulo está no ar</strong>: não há menu, não há dado, e nada
          do que estava na sua frente antes desta mensagem pode ser considerado atual.
        </p>
        <p style={{ margin: '0 0 1rem', fontSize: '.9rem' }}>
          Recarregue a página. Se a falha voltar, é defeito de software: vale abrir um
          chamado com o horário e o que você estava fazendo. Enquanto isso, não tire
          conclusão sobre a frota, o pátio ou a subestação a partir desta janela.
        </p>
        <p style={{ margin: '0 0 1.25rem' }}>
          <code
            style={{
              display: 'block',
              padding: '.5rem .75rem',
              borderRadius: '4px',
              background: 'var(--nx-bg-sunken, #eef0f6)',
              color: 'var(--nx-mute-strong, #4c5266)',
              fontFamily: 'var(--nx-font-mono, monospace)',
              fontSize: '.75rem',
              overflowWrap: 'anywhere',
            }}
          >
            {texto}
            {foiError ? '' : ' [o valor lançado não era um Error]'}
          </code>
        </p>
        {/*
          `<a href>` e não botão com `useNavigate`: recarregar de verdade é o que
          desmonta o estado quebrado, e um `href` funciona mesmo que o roteador
          seja o que quebrou. `reload` no `onClick` é conveniência, não requisito
          — sem JS o link ainda vai para a raiz do portal.
        */}
        <a
          href="/app"
          style={{
            display: 'inline-block',
            padding: '10px 16px',
            borderRadius: '6px',
            background: 'var(--nx-btn-primary-bg, #b83f00)',
            color: 'var(--nx-btn-primary-fg, #ffffff)',
            fontWeight: 600,
            fontSize: '.875rem',
            textDecoration: 'none',
          }}
        >
          Recarregar o portal
        </a>
      </div>
    );
  }
}
